import { useContext, useEffect, useMemo } from "react";
import {
  Card,
  Content,
  Text,
  Form,
  TextField,
  ProgressCircle,
  DialogContainer,
} from "@react-spectrum/s2";

import { WIZARD_STEP } from "../wizardSteps";

import { VideoWizardContext } from "@app/views/projects/videoWizardContext";
import { ImageVariations } from "../ImageVariations";
import { EditVariationDialog } from "../EditVariationDialog";
import { Step } from "./Step";
import { DevToolbarItem } from "../DevToolbar";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

/** Stable empty fallback, so the memos below don't see a new array each render. */
const NONE = [];

export const ImagesVariationsStep = ({
  name,
  onContinue,
  onBack,
  continueLabel,
}) => {
  const {
    template,
    video,
    updateVideo,
    selectedCampaign,
    customerName,
    taglinesData,
    handleTaglineChange,
    editVariation,
    setEditVariation,
    handleUploadAssets,
    stepActionCtx,
  } = useContext(VideoWizardContext);

  // Generation is owned by the wizard (see `useImageVariations`), so it starts
  // when the step is entered and keeps going while it is displayed. Everything
  // rendered below — the reference groups of this step's stages, their
  // variations and their progress — comes from there.
  const imageVariations = stepActionCtx?.imageVariations;

  const stageGroups = imageVariations?.groups ?? NONE;
  const stageVariations = imageVariations?.variations ?? NONE;

  // Entering the step arms the pipeline through the step action. Landing on it
  // directly — a reload, or a resumed project — never goes through that, so it
  // is armed here too; arming twice is a no-op.
  const { run } = imageVariations ?? {};

  useEffect(() => {
    run?.();
  }, [run]);

  const isContinueDisabled = useMemo(
    () =>
      !stageVariations.length ||
      stageVariations.some(
        (variation) =>
          !video.data?.assets?.find?.((img) => img.name === variation.key),
      ),
    [stageVariations, video.data?.assets],
  );

  const isFireflyKeyVisuals = useMemo(() => {
    console.log("step", stepActionCtx);
    return (
      video.data?.templateId === "firefly-enterprise" &&
      video.data?.currentStep === WIZARD_STEP.IMAGES_PLANNING
    );
  }, [video.data?.templateId, video.data?.currentStep]);

  return (
    <>
      <Step
        styles={style({
          gridArea: "content",
          display: "flex",
          flexDirection: "column",
        })}
      >
        <Step.Header
          title={name ?? "Images variations"}
          subtitle={
            isFireflyKeyVisuals ? (
              <>
                Confirm the cleaned, upscaled version of your reference photo.
                <br />
                Once confirmed, it's split into a background and an isolated
                subject, then generated into variations.
              </>
            ) : (
              <>
                Review the generated image variations for each campaign.
                <br />
                You can edit the variations or generate new ones if needed.
              </>
            )
          }
        />

        <div
          className={style({
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 24,
          })}
        >
          {stageGroups.map((group, idx) => (
            <div
              key={group.id}
              className={style({
                display: "flex",
                gap: 48,
                boxShadow: "emphasized",
                boxSizing: "border-box",
                maxWidth: "full",
                marginX: "auto",
                padding: 24,
                borderRadius: "lg",
                backgroundColor: "base",
              })}
            >
              {video.data?.templateId === "csc-overview" && (
                <div>
                  <Card
                    size="XL"
                    variant="quiet"
                    styles={style({ width: 240 })}
                  >
                    <Content>
                      <Text slot="title">{`Campaign variation ${idx + 1}`}</Text>
                      <Form styles={style({ marginTop: 20 })}>
                        <TextField
                          label="Campaign name"
                          value={
                            taglinesData[idx]?.name ??
                            selectedCampaign?.taglines?.[idx]?.name ??
                            `${customerName} ${template.data?.campaignLines?.[idx] || `${idx + 1} campaign`}`
                          }
                          onChange={(value) =>
                            handleTaglineChange(idx, "name", value)
                          }
                        />
                        {!!selectedCampaign?.taglines?.length ? (
                          <>
                            <TextField
                              label="Tagline"
                              defaultValue={
                                taglinesData[idx]?.en ??
                                selectedCampaign?.taglines?.[idx]?.en
                              }
                              onChange={(value) =>
                                handleTaglineChange(idx, "en", value)
                              }
                            />
                            <TextField
                              label="Tagline"
                              contextualHelp={
                                <Text
                                  slot="description"
                                  styles={style({ font: "detail-sm" })}
                                >
                                  (ES)
                                </Text>
                              }
                              defaultValue={
                                taglinesData[idx]?.es ??
                                selectedCampaign?.taglines?.[idx]?.es
                              }
                              onChange={(value) =>
                                handleTaglineChange(idx, "es", value)
                              }
                            />
                          </>
                        ) : (
                          <div
                            className={style({
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              marginStart: 8,
                            })}
                          >
                            <ProgressCircle
                              isIndeterminate
                              size="S"
                              staticColor="auto"
                              aria-label="Generating taglines"
                            />
                            <Text styles={style({ font: "detail" })}>
                              Generating taglines
                            </Text>
                          </div>
                        )}
                      </Form>
                    </Content>
                  </Card>
                </div>
              )}

              {typeof selectedCampaign?.id !== "undefined" ? (
                <ImageVariations
                  key={group.id}
                  group={group}
                  assets={video.data?.assets}
                  referenceUrlByPath={imageVariations?.referenceUrlByPath}
                  generatingKeys={imageVariations?.generatingKeys}
                  skipImageLabeling={imageVariations?.skipImageLabeling}
                  isLabeled={imageVariations?.labeling?.isLabeled}
                  templateData={template.data}
                  isLoading={video.isLoading}
                  onEditVariation={setEditVariation}
                  onUploadReference={handleUploadAssets}
                  onRegenerate={() => run?.({ force: true, keys: group.keys })}
                />
              ) : null}
            </div>
          ))}
        </div>
      </Step>

      <DialogContainer onDismiss={() => setEditVariation(null)}>
        {!!editVariation && (
          <EditVariationDialog
            key={JSON.stringify([video.data?.id, editVariation.key])}
            videoId={video.data?.id}
            variationKey={editVariation.key}
            referenceKey={editVariation.referenceKey}
            isReferenceAsset={!!editVariation.isReferenceAsset}
            onUpload={handleUploadAssets}
          />
        )}
      </DialogContainer>

      <Step.Actions
        onBack={onBack}
        onContinue={onContinue}
        continueVariant="primary"
        continueLabel={continueLabel}
        isContinueDisabled={isContinueDisabled}
      />

      <DevToolbarItem
        isPending={updateVideo.isPending}
        onPress={() => {
          const keys = Array.from(
            stageVariations
              .filter((variation) => !!variation.type)
              .reduce((acc, variation) => acc.add(variation.key), new Set()),
          );

          const assets = video.data?.assets
            ?.filter((asset) => !keys.includes(asset.name))
            ?.map(({ labeling, ...asset }) => asset);

          updateVideo.mutate({
            audiencesVariations: null,
            abstractVariations: null,
            // Keep the variations belonging to the other stages intact.
            imageVariations:
              video.data?.imageVariations?.filter(
                (variation) => !keys.includes(variation.key),
              ) ?? [],
            templateAssetsRenditions: [],
            templateAssetsRenditionFiles: [],
            assets,
          });
        }}
      >
        Regenerate visuals
      </DevToolbarItem>
      <DevToolbarItem
        isPending={template.isPending}
        onPress={() => template.refetch()}
      >
        Refetch template
      </DevToolbarItem>
    </>
  );
};
